// @ts-nocheck
import * as React from "react";
import { Logo, siteName, brandParts, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, useCurrentUser, logout, AppFooter, PhoneInput, apiErrorMessage, fmtDuration } from "./common";
import { PUBLIC_NAV, MobileNavMenu, useIsNarrow } from "./public-pages";
import { NaverMapCanvas } from "../components/naver/NaverMapCanvas";
import { useNaverMarkers } from "../components/naver/useNaverMarkers";
import { MarkerGlyph } from "../components/naver/MarkerGlyph";
import { MARKER_HEX, glyphMarkup } from "../components/naver/markers";

const CAT_COLOR = { hospital: "rose", health: "green", fire: "orange", welfare: "sky", care: "amber" };
const CAT_LABEL = { hospital: "병", health: "보", fire: "소", welfare: "복", care: "요" };
const RISK_COLOR = { critical: "rose", warning: "amber", stable: "green" };
const MGR_STATUS_COLOR = { available: "green", overloaded: "rose", busy: "amber", moving: "amber", offline: "gray" };

// 랜딩 "권역 운영 지도" — 가짜 SVG 투영을 실제 네이버 지도로 교체.
// 공개 페이지의 쇼케이스 티저라 조작은 잠그고(interactive=false) "지도 전체 보기 →"
// (/map)로 위임한다. hero/feature 아래 fold 밖이라 IntersectionObserver lazy 마운트로
// 첫 페인트 시 타일 요청을 미룬다. 데이터(/api/v1/map/points)는 비로그인 anon-safe라
// 익명 방문자에겐 좌표+분류/위험도만(이름 등 PII는 null) 내려온다.
// minFitZoom: 익명 기본 권역(서울)은 bbox가 작은데 카드가 가로로 넓어, 순수
// fitBounds는 세로에 맞추느라 z10까지 줌아웃 → 서울이 주변 경기에 파묻혀 보인다.
// z11 바닥을 줘 서울이 프레임을 채우게 한다(중심 유지, 서울 전 기관 여전히 노출).
function LandingRegionMap({ mapData }) {
  const wrapRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [map, setMap] = React.useState(null);

  React.useEffect(() => {
    if (inView) return;
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  const center = mapData?.center;
  const bbox = mapData?.bbox || null;

  const sig = React.useMemo(() => {
    if (!mapData) return "";
    return JSON.stringify({
      o: (mapData.organizations || []).map(x => [x.id, x.lat, x.lng, x.category]),
      e: (mapData.elderlies || []).map(x => [x.id, x.lat, x.lng, x.risk_level]),
      m: (mapData.care_managers || []).map(x => [x.id, x.lat, x.lng, x.status]),
    });
  }, [mapData]);

  // 권역 운영 지도 마커 — 다른 지도(/map·대시보드)와 동일하게 공유 useNaverMarkers
  // 훅에 위임해 그리드 클러스터링(겹치는 마커를 묶음 숫자 원으로 표시)을 적용한다.
  // 쇼케이스 티저라 전부 노출(rendered)·비강조(dim=false)·선택상태 없음.
  // clustering:true여도 같은 셀에 2개 이상 겹칠 때만 묶이므로 희소 지역은 개별 유지.
  const allVisible = (list) => {
    const m = new Map();
    (list || []).forEach(x => { if (x.lat != null && x.lng != null) m.set(x.id, { rendered: true, dim: false }); });
    return m;
  };
  const orgVis     = React.useMemo(() => allVisible(mapData?.organizations), [sig]);
  const elderlyVis = React.useMemo(() => allVisible(mapData?.elderlies),     [sig]);
  const managerVis = React.useMemo(() => allVisible(mapData?.care_managers), [sig]);

  useNaverMarkers({
    map,
    data: mapData,
    orgVisibility: orgVis,
    elderlyVisibility: elderlyVis,
    managerVisibility: managerVis,
    selected: null,
    onSelect: () => {},
    clustering: true,
  });

  // wrapRef는 항상 렌더해야 IntersectionObserver가 첫 마운트에 붙는다(데이터 로딩 분기에서
  // early-return하면 ref가 null이라 observer가 영영 안 붙고 inView가 false로 고정됨).
  return (
    <div ref={wrapRef} style={{position:'absolute', inset:0}}>
      {!mapData
        ? <div className="muted" style={{position:'absolute', inset:0, display:'grid', placeItems:'center'}}>지도 데이터 불러오는 중…</div>
        : (inView && center)
          ? <NaverMapCanvas center={center} bbox={bbox} zoom={13} interactive={false} fitPadding={30} minFitZoom={11} onMapReady={setMap}/>
          : <div style={{position:'absolute', inset:0, display:'grid', placeItems:'center'}}>
              <span className="muted" style={{fontSize:12, fontWeight:600, background:'rgba(255,255,255,0.85)', padding:'6px 12px', borderRadius:8}}>지도 준비 중…</span>
            </div>}
    </div>
  );
}

function LandingDesktop() {
  const [stats, setStats] = React.useState(null);
  const [mapData, setMapData] = React.useState(null);
  const { user } = useCurrentUser();
  // 좁은 화면(태블릿 세로 이하)에서는 상단 메뉴를 햄버거 드로어로 접는다.
  const narrow = useIsNarrow(860);

  React.useEffect(() => {
    fetch("/api/v1/landing/stats").then(r => r.json()).then(setStats);
    fetch("/api/v1/map/points").then(r => r.json()).then(setMapData);
  }, []);

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      {/* Top nav — 넓은 화면은 인라인 메뉴, 좁은 화면은 햄버거 드로어(MobileNavMenu).
          (과거: 고정 flex라 390px에서 메뉴 글자가 세로로 짜부라졌다) */}
      <header style={{display:'flex', alignItems:'center', flexWrap:'wrap', rowGap:10, padding:'18px clamp(18px, 5vw, 64px)', borderBottom:'1px solid var(--border-soft)', background:'var(--bg)'}}>
        <a href="/" style={{display:'flex', alignItems:'center', gap:10, textDecoration:'none', color:'inherit'}}>
          <div className="brand-mark" style={{width:32, height:32}}>{brandParts(siteName()).mark}</div>
          <div>
            <div style={{fontWeight:800, fontSize:15, color:'var(--ink-1)'}}>{brandParts(siteName()).name}</div>
            {brandParts(siteName()).sub && <div style={{fontSize:10, color:'var(--ink-3)', letterSpacing:'0.06em', marginTop:-2}}>{brandParts(siteName()).sub}</div>}
          </div>
        </a>
        {/* 상단 메뉴는 공개 소개 페이지(public-pages.tsx)와 PUBLIC_NAV 한 배열을 공유한다.
            과거에는 전부 #features/#partners 앵커라 실제로 가는 곳이 없었다. */}
        {!narrow && (
          <nav style={{display:'flex', gap:'clamp(14px, 2.2vw, 28px)', marginLeft:'clamp(16px, 4vw, 56px)', fontSize:13, color:'var(--ink-2)', fontWeight:500,
                       flex:'1 1 200px', minWidth:0, overflowX:'auto', whiteSpace:'nowrap', WebkitOverflowScrolling:'touch'}}>
            {PUBLIC_NAV.map(n => (
              <a key={n.href} href={n.href} style={{color:'inherit', textDecoration:'none'}}>{n.label}</a>
            ))}
          </nav>
        )}
        <div style={{marginLeft:'auto', display:'flex', gap:10, alignItems:'center'}}>
          {narrow
            ? <MobileNavMenu/>
            : user ? <UserMenu user={user}/> : (
                <>
                  <a href="/login" className="btn btn-ghost btn-md">로그인</a>
                  <a href="/signup" className="btn btn-primary btn-md">회원가입</a>
                </>
              )}
        </div>
      </header>

      {/* Hero — 비로그인은 2단(좌 카피 + 우 케어신청 폼), 로그인은 카피 1단(폼 제거) */}
      <section style={{padding:'clamp(32px, 6vw, 68px) clamp(18px, 5vw, 64px) clamp(32px, 5vw, 56px)', display:'grid',
                        gridTemplateColumns: user ? '1fr' : 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap:'clamp(28px, 4vw, 48px)'}}>
        <div>
          <div style={{display:'inline-flex', alignItems:'center', gap:8, padding:'6px 14px', background:'var(--orange-50)', color:'var(--orange-700)', borderRadius:999, fontSize:12, fontWeight:700, marginBottom:24}}>
            <span className="dot" style={{background:'var(--orange-500)'}}/> 2026 돌봄통합지원법 시행 대비
          </div>
          <h1 style={{fontSize:'clamp(30px, 6.4vw, 54px)', lineHeight:1.15, wordBreak:'keep-all', letterSpacing:'-0.03em', fontWeight:800, color:'var(--ink-1)', margin:0}}>
            한 권역의 돌봄을<br/>
            <span style={{color:'var(--orange-600)'}}>하나의 플랫폼</span>에서.
          </h1>
          <p style={{fontSize:'clamp(14px, 1.4vw, 17px)', lineHeight:1.65, color:'var(--ink-2)', marginTop:24, maxWidth:520}}>
            어르신의 상태를 AI가 분석해 가장 적합한 요양기관과 케어매니저를 자동으로 매칭합니다.
            권역 협회·유관기관·매니저·보호자가 한 화면에서 함께 일합니다.
          </p>
          <div style={{display:'flex', gap:12, marginTop:32, flexWrap:'wrap'}}>
            <a href={user ? user.default_path : "/adopt"} className="btn btn-primary btn-lg" style={{textDecoration:'none'}}>{user ? "내 화면으로 이동 →" : "권역 도입 상담 신청 →"}</a>
            {!user && <a href="/about" className="btn btn-ghost btn-lg" style={{textDecoration:'none'}}>서비스 소개 보기</a>}
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:'clamp(14px, 2vw, 32px)', marginTop:40, paddingTop:24, borderTop:'1px solid var(--border-soft)'}}>
            <Stat n={stats?.active_regions ?? '·'} l="활성 권역"/>
            <Stat n={stats?.total_managers?.toLocaleString() ?? '·'} l="등록 케어매니저"/>
            <Stat n={stats ? `${stats.success_rate}%` : '·'} l="평균 매칭 성공률"/>
            <Stat n={stats ? fmtDuration(stats.avg_response_mins) : '·'} l="평균 매칭 응답"/>
          </div>
        </div>

        {/* 비로그인만 케어 신청 안내 카드 노출 — 케어 신청은 회원가입으로 접수한다. */}
        {!user && <CareSignupCTA stats={stats}/>}
      </section>

      {/* Feature row */}
      <section id="features" style={{padding:'24px clamp(18px, 5vw, 64px) clamp(40px, 5vw, 60px)', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(230px, 1fr))', gap:18}}>
        {[
          {t:'AI 자동매칭 (COD)', d:'어르신 상태·자격·거리·매니저 부하를 가중치로 평가해 최적 매니저를 추천합니다.', i:'match', c:'orange', href:'/cod'},
          {t:'권역 기반 운영',   d:'권역 협회 관리자가 케이스·기관·매니저를 한 화면에서 통합 운영합니다.', i:'region', c:'green',  href:'/dashboard/region'},
          {t:'지도·이동 최적화', d:'유관기관·매니저 위치를 실시간으로 확인하고 동선을 줄입니다.', i:'map', c:'sky',    href:'/map'},
          {t:'보호자 안심 알림', d:'방문 시작·종료·이상 신호를 가족에게 PWA로 즉시 알립니다.', i:'heart', c:'amber',href:'/m/notify'},
        ].map((f,i)=>(
          <a key={i} href={f.href} className="card card-pad" style={{borderRadius:18, textDecoration:'none', color:'inherit'}}>
            <span style={{width:40, height:40, borderRadius:12, background:`var(--${f.c}-50)`, color:`var(--${f.c}-${f.c==='amber'?'500':'600'})`, display:'grid', placeItems:'center'}}>
              <span style={{width:22, height:22, display:'inline-block'}}>{Ic[f.i]}</span>
            </span>
            <div style={{fontWeight:700, fontSize:15, marginTop:14}}>{f.t}</div>
            <div style={{fontSize:12, color:'var(--ink-3)', lineHeight:1.55, marginTop:6}}>{f.d}</div>
            <div style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, marginTop:10}}>자세히 →</div>
          </a>
        ))}
      </section>

      {/* Region map — live markers from DB */}
      <section id="map-section" style={{padding:'24px clamp(18px, 5vw, 64px) clamp(40px, 5vw, 60px)'}}>
        <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:12, flexWrap:'wrap', marginBottom:18}}>
          <div>
            <div style={{fontSize:11, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em', marginBottom:6}}>권역 운영 지도</div>
            <div className="h2" style={{fontSize:24, letterSpacing:'-0.02em'}}>권역 안의 매니저·기관·어르신을 한 눈에</div>
            <div className="muted" style={{fontSize:13, marginTop:6}}>{mapData ? `유관기관 ${mapData.organizations.length.toLocaleString()} · 케어매니저 ${mapData.care_managers.length.toLocaleString()} · 어르신 ${mapData.elderlies.length.toLocaleString()}` : '로드 중…'}</div>
          </div>
          <a href="/map" className="btn btn-ghost btn-md" style={{textDecoration:'none'}}>지도 전체 보기 →</a>
        </div>

        <div className="card" style={{display:'flex', flexWrap:'wrap', borderRadius:18, overflow:'hidden', border:'1px solid var(--border-soft)'}}>
          {/* Map area — 좁아지면 범례가 지도 아래로 내려간다(그리드 고정 시 지도 폭이 0에 수렴). */}
          <div style={{position:'relative', flex:'1 1 380px', minWidth:0, background:'#E7E1CF', minHeight:'clamp(300px, 46vw, 480px)', overflow:'hidden'}}>
            <LandingRegionMap mapData={mapData}/>
          </div>

          {/* Legend panel */}
          <div className="landing-map-legend" style={{padding:'22px 22px', flex:'1 1 280px', minWidth:0, background:'var(--surface)', display:'flex', flexDirection:'column'}}>
            <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:10}}>범례</div>

            {/* Care managers */}
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12}}>
              <div style={{width:30, height:30, borderRadius:'50%', background:'linear-gradient(135deg, var(--orange-300), var(--orange-500))', display:'grid', placeItems:'center', border:'2px solid #fff', boxShadow:'0 2px 6px rgba(0,0,0,0.18)'}}>
                <MarkerGlyph glyph="manager" size={16} bg={MARKER_HEX.manager}/>
              </div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:12.5, fontWeight:700}}>케어매니저</div>
                <div className="muted" style={{fontSize:10}}>실시간 위치 + 가용 상태</div>
              </div>
              <span className="mono" style={{fontSize:11, fontWeight:700}}>{(mapData?.care_managers.length ?? 0).toLocaleString()}</span>
            </div>

            {/* Status dots */}
            <div style={{display:'flex', gap:8, padding:'8px 10px', background:'var(--bg-sub)', borderRadius:8, marginBottom:14, fontSize:10, color:'var(--ink-3)'}}>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--green-500)'}}/> 가용</span>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--amber-500)'}}/> 이동중</span>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--rose-500)'}}/> 과부하</span>
            </div>

            {/* Elderly */}
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12}}>
              {/* 지도 어르신 마커의 축소판 — 핀 + 지팡이 짚은 사람. */}
              <svg width="22" height="28" viewBox="0 0 28 36" style={{width:22, height:28, flex:'none'}}>
                <path d="M14 0C6.3 0 0 6.3 0 14c0 10 14 22 14 22s14-12 14-22C28 6.3 21.7 0 14 0Z" fill="var(--rose-500)" stroke="#fff" strokeWidth="2"/>
                <g transform="translate(7.1,7.1) scale(0.575)"
                   dangerouslySetInnerHTML={{__html: glyphMarkup('elderly')}}/>
              </svg>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:12.5, fontWeight:700}}>어르신 케이스</div>
                <div className="muted" style={{fontSize:10}}>위험도별 색상</div>
              </div>
              <span className="mono" style={{fontSize:11, fontWeight:700}}>{(mapData?.elderlies.length ?? 0).toLocaleString()}</span>
            </div>
            <div style={{display:'flex', gap:8, padding:'8px 10px', background:'var(--bg-sub)', borderRadius:8, marginBottom:14, fontSize:10, color:'var(--ink-3)'}}>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--rose-500)'}}/> 위험</span>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--amber-500)'}}/> 주의</span>
              <span style={{display:'flex', alignItems:'center', gap:4}}><span className="dot" style={{background:'var(--green-500)'}}/> 안정</span>
            </div>

            {/* Organizations by category */}
            <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginTop:4, marginBottom:8}}>유관기관 ({(mapData?.organizations.length ?? 0).toLocaleString()})</div>
            {[
              { key: 'hospital', label: '병원·의원',     icon: 'hospital' },
              { key: 'health',   label: '보건소·보건지소', icon: 'health' },
              { key: 'fire',     label: '소방·119',     icon: 'fire' },
              { key: 'welfare',  label: '복지관·주민센터', icon: 'welfare' },
              { key: 'care',     label: '요양기관',      icon: 'care' },
            ].map(c => {
              const count = (mapData?.organizations || []).filter(o => o.category === c.key).length;
              const hex = MARKER_HEX.category[c.key];
              return (
                <div key={c.key} style={{display:'flex', alignItems:'center', gap:10, padding:'7px 0', borderTop:'1px solid var(--border-soft)'}}>
                  {/* 지도 마커와 같은 색·픽토그램(markers.ts 단일 출처). */}
                  <span style={{width:24, height:24, borderRadius:6, background:hex, display:'grid', placeItems:'center', boxShadow:'0 2px 4px rgba(0,0,0,0.12)'}}>
                    <MarkerGlyph glyph={c.icon} size={13} bg={hex}/>
                  </span>
                  <span style={{flex:1, fontSize:12}}>{c.label}</span>
                  <span className="mono muted" style={{fontSize:11}}>{count.toLocaleString()}</span>
                </div>
              );
            })}

            <div style={{flex:1}}/>
            <div style={{marginTop:16, padding:'8px 10px', background:'var(--orange-50)', color:'var(--orange-700)', borderRadius:8, fontSize:10.5, lineHeight:1.5}}>
              ⓘ 권역 경계는 <b>주황 점선</b>으로 표시됩니다. 권역 안의 모든 자원이 한 화면에 모입니다.
            </div>
          </div>
        </div>
      </section>

      {/* Partners — live count from DB */}
      <section id="partners" style={{padding:'0 clamp(18px, 5vw, 64px) clamp(40px, 5vw, 60px)'}}>
        <div className="card" style={{padding:'clamp(22px, 3vw, 32px) clamp(20px, 3vw, 36px)', borderRadius:18, background:'linear-gradient(120deg, var(--bg-sub), var(--surface))', border:'1px solid var(--border-soft)'}}>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'clamp(20px, 3vw, 32px)', alignItems:'center'}}>
            <div>
              <div style={{fontSize:11, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em', marginBottom:8}}>운영 현황</div>
              <div className="h2" style={{fontSize:22}}>전국 권역에서 매일 사용되는<br/>통합돌봄 운영 플랫폼</div>
            </div>
            <KpiCol n={stats?.monthly_matches?.toLocaleString() ?? '·'} l="이달 매칭 시도"/>
            <KpiCol n={stats?.partner_orgs?.toLocaleString() ?? '·'}     l="등록 유관기관"/>
            <KpiCol n={`${stats?.success_rate ?? '·'}%`}                 l="평균 매칭 성공"/>
          </div>
        </div>
      </section>

      <AppFooter/>
    </div>
  );
}

function Stat({n, l}) {
  return (
    <div>
      <div style={{fontSize:24, fontWeight:800, color:'var(--ink-1)', fontVariantNumeric:'tabular-nums'}}>{n}</div>
      <div style={{fontSize:11, color:'var(--ink-3)'}}>{l}</div>
    </div>
  );
}
function KpiCol({n, l}) {
  return (
    <div>
      <div style={{fontSize:32, fontWeight:800, color:'var(--orange-600)', fontVariantNumeric:'tabular-nums', lineHeight:1}}>{n}</div>
      <div style={{fontSize:12, color:'var(--ink-3)', marginTop:6, fontWeight:500}}>{l}</div>
    </div>
  );
}

// ─── Care signup CTA ──────────────────────────────────────
// 케어 신청은 회원가입으로만 접수한다. 비로그인 방문자에게 회원가입 동선을 안내한다.
function CareSignupCTA({ stats }) {
  const STEPS = [
    { n: '01', t: '회원가입', d: '보호자·어르신으로 가입하며 어르신 정보를 등록합니다.' },
    { n: '02', t: 'AI 매칭 추천', d: '권역 관리자가 검토하고 최적 매니저를 배정합니다.' },
    { n: '03', t: '방문 · 알림', d: '매니저가 방문하고 진행 상황을 실시간으로 공유합니다.' },
  ];
  return (
    <div style={{position:'relative'}}>
      <div className="card" style={{padding:30, borderRadius:24, boxShadow:'var(--shadow-lg)', minHeight:540, display:'flex', flexDirection:'column'}}>
        <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:18}}>
          <span style={{width:44, height:44, borderRadius:12, background:'linear-gradient(135deg, var(--orange-50), var(--orange-100))', color:'var(--orange-600)', display:'grid', placeItems:'center'}}>
            <span style={{width:24, height:24, display:'inline-block'}}>{Ic.heart}</span>
          </span>
          <div>
            <div style={{fontWeight:700, fontSize:15}}>케어 신청</div>
            <div style={{fontSize:11, color:'var(--ink-3)'}}>평균 응답 {stats ? fmtDuration(stats.avg_response_mins) : '·'} · 성공률 {stats?.success_rate ?? '·'}%</div>
          </div>
        </div>

        <div className="h2" style={{fontSize:22, lineHeight:1.35, marginBottom:8}}>회원가입으로<br/>케어를 신청하세요</div>
        <div className="muted" style={{fontSize:13, lineHeight:1.65, marginBottom:20}}>
          회원가입 시 어르신 정보를 등록하면 케어 신청이 함께 접수됩니다. 가입 후 홈 화면에서
          신청 상태(승인 대기·케이스 준비 중)와 배정된 매니저를 직접 확인할 수 있습니다.
        </div>

        <div style={{display:'flex', flexDirection:'column', gap:0}}>
          {STEPS.map((s, i) => (
            <div key={s.n} style={{display:'flex', gap:14, padding:'14px 0', borderBottom: i < STEPS.length - 1 ? '1px solid var(--border-soft)' : 'none'}}>
              <div style={{flex:'0 0 40px', height:40, borderRadius:12, background:'var(--orange-50)', color:'var(--orange-700)', display:'grid', placeItems:'center', fontSize:13, fontWeight:800}}>{s.n}</div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:14, fontWeight:800}}>{s.t}</div>
                <div className="muted" style={{fontSize:12, lineHeight:1.55, marginTop:2}}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{flex:1}}/>

        <a href="/signup" className="btn btn-primary btn-lg" style={{width:'100%', textDecoration:'none', display:'flex', justifyContent:'center', marginTop:18}}>회원가입하고 케어 신청 →</a>
        <div style={{textAlign:'center', fontSize:11, color:'var(--ink-3)', marginTop:10}}>
          이미 회원이신가요? <a href="/login" style={{color:'var(--ink-1)', fontWeight:700, textDecoration:'none'}}>로그인</a>
        </div>
      </div>
    </div>
  );
}

function UserMenu({ user }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const initial = (user.name || "?").charAt(0);
  return (
    <div ref={ref} style={{position:'relative', display:'flex', alignItems:'center', gap:8}}>
      <a href={user.default_path} className="btn btn-primary btn-md" style={{textDecoration:'none'}}>내 화면으로 →</a>
      <button onClick={()=>setOpen(o=>!o)}
        style={{display:'flex', alignItems:'center', gap:8, padding:'4px 10px 4px 4px',
          borderRadius:999, border:'1px solid var(--border-soft)', background:'var(--surface)',
          cursor:'pointer'}}>
        <span style={{width:28, height:28, borderRadius:'50%',
          background:'linear-gradient(135deg, var(--orange-400), var(--orange-600))',
          color:'#fff', display:'grid', placeItems:'center', fontSize:12, fontWeight:800}}>{initial}</span>
        <span style={{display:'flex', flexDirection:'column', alignItems:'flex-start', lineHeight:1.15}}>
          <span style={{fontSize:12, fontWeight:700, color:'var(--ink-1)'}}>{user.name}</span>
          <span style={{fontSize:10, color:'var(--ink-3)'}}>{user.role_label}</span>
        </span>
        <span style={{color:'var(--ink-3)', marginLeft:2}}>{Ic.caret}</span>
      </button>
      {open && (
        <div style={{position:'absolute', top:'calc(100% + 8px)', right:0, minWidth:200,
          background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:12,
          boxShadow:'0 12px 32px rgba(20,16,12,0.12)', padding:6, zIndex:30}}>
          <div style={{padding:'10px 12px 8px', borderBottom:'1px solid var(--border-soft)'}}>
            <div style={{fontSize:12, fontWeight:700, color:'var(--ink-1)'}}>{user.name}</div>
            <div style={{fontSize:11, color:'var(--ink-3)', marginTop:2}}>{user.email}</div>
          </div>
          <a href={user.default_path} style={{display:'block', padding:'10px 12px', fontSize:12.5,
            color:'var(--ink-1)', textDecoration:'none', borderRadius:8}}
            onMouseEnter={e=>e.currentTarget.style.background='var(--bg-sub)'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>내 화면으로 이동</a>
          <button onClick={logout} style={{width:'100%', textAlign:'left', padding:'10px 12px',
            fontSize:12.5, color:'var(--rose-500)', background:'transparent', border:'none',
            borderRadius:8, cursor:'pointer'}}
            onMouseEnter={e=>e.currentTarget.style.background='var(--bg-sub)'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>로그아웃</button>
        </div>
      )}
    </div>
  );
}

export { LandingDesktop };
